import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(2)}L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function fmtShort(n) {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(1)}L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:8, padding:'8px 12px', fontSize:12, boxShadow:'0 2px 12px rgba(0,0,0,0.08)' }}>
      <div style={{ color:'#888', marginBottom:4 }}>{label}</div>
      <div style={{ color:'#6B1C2A', fontWeight:600 }}>{fmt(payload[0]?.value)}</div>
    </div>
  );
};

const FILTERS = ['ALL','6M','3M','1M'];

export default function TrendChart({ data, filter, onFilterChange }) {
  const chartData = (data || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('en-IN', { day:'numeric', month:'short' }),
    value: d.portfolio_value || d.collateral_value,
    raw:   d.portfolio_value || d.collateral_value,
  }));

  // Y-axis scaling — start from min - 5%, not zero
  const values = chartData.map(d => d.value).filter(Boolean);
  const minVal  = values.length ? Math.min(...values) : 0;
  const maxVal  = values.length ? Math.max(...values) : 0;
  const padding = (maxVal - minVal) * 0.1 || maxVal * 0.05;
  const yMin    = Math.max(0, minVal - padding);
  const yMax    = maxVal + padding;

  return (
    <div style={{ padding:'14px 16px', height:280, boxSizing:'border-box' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:10, fontWeight:600, color:'#888', textTransform:'uppercase', letterSpacing:'0.08em' }}>
          Portfolio Value Trend
        </div>
        <div style={{ display:'flex', gap:4 }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => onFilterChange && onFilterChange(f)}
              style={{ padding:'2px 8px', fontSize:10, borderRadius:4, border:'1px solid', cursor:'pointer', fontWeight: filter===f ? 600 : 400, background: filter===f ? '#6B1C2A' : 'transparent', color: filter===f ? '#fff' : '#888', borderColor: filter===f ? '#6B1C2A' : '#e0d8d5' }}>
              {f}
            </button>
          ))}
        </div>
      </div>
      {chartData.length < 2 ? (
        <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#bbb', fontSize:12 }}>
          Not enough data points yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top:4, right:8, left:0, bottom:0 }}>
            <defs>
              <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6B1C2A" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#6B1C2A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f0ee" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize:9, fill:'#aaa' }} axisLine={false} tickLine={false} />
            <YAxis
              domain={[yMin, yMax]}
              tickFormatter={fmtShort}
              tick={{ fontSize:9, fill:'#aaa' }}
              axisLine={false} tickLine={false}
              width={56}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone" dataKey="value"
              stroke="#6B1C2A" strokeWidth={2}
              fill="url(#trendGrad)"
              dot={{ r:3, fill:'#6B1C2A', strokeWidth:0 }}
              activeDot={{ r:5, fill:'#6B1C2A' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
